import { AnalyticsEvent } from "../analytics_types.js";
import { AnalyticsTransport } from "./analytics_transport.js";
import { stringifyWithForcedFloatFields } from "./json_float_stringify.js";
import { CustomEventsController } from "../../socket_handler/controllers/custom_events_controller.js";

export type AthenaProxyTransportOptions = {
  endpoint_url?: string;
  timeout_ms?: number;
  include_raw_event?: boolean;
  debug?: boolean;
  debug_max_items?: number;
  is_qa_override?: boolean;
};

const toIso = ( ts_ms: number ): string =>
{
  try
  {
    return new Date( ts_ms ).toISOString();
  }
  catch
  {
    return '';
  }
};

const toStringSafe = ( v: unknown ): string | undefined =>
{
  if ( v == null ) return undefined;
  try
  {
    return String( v );
  }
  catch
  {
    return undefined;
  }
};

const toIntSafe = ( v: unknown ): number | undefined =>
{
  if ( typeof v === 'number' && Number.isFinite( v ) ) return Math.floor( v );
  if ( typeof v === 'string' )
  {
    const n = Number( v );
    if ( Number.isFinite( n ) ) return Math.floor( n );
  }
  return undefined;
};

const toIsoFromUnknown = ( v: unknown ): string | undefined =>
{
  if ( v == null ) return undefined;
  if ( typeof v === 'number' && Number.isFinite( v ) )
  {
    return toIso( v );
  }
  if ( typeof v === 'string' )
  {
    const parsed = Date.parse( v );
    if ( Number.isFinite( parsed ) ) return toIso( parsed );
  }
  return undefined;
};

const normalizeVersion = ( v: unknown ): string =>
{
  const raw = toStringSafe( v )?.trim() ?? '';
  if ( raw.length === 0 ) return '0_0_0';

  // Guard against accidental datetime values in version column.
  if ( /^\d{4}-\d{2}-\d{2}t/i.test( raw ) ) return '0_0_0';
  if ( /^\d{4}-\d{2}-\d{2}$/.test( raw ) ) return '0_0_0';

  const underscored = raw.replace( /[^a-zA-Z0-9]+/g, '_' ).replace( /^_+|_+$/g, '' );
  if ( underscored.length === 0 ) return '0_0_0';
  return underscored;
};

const isPrimitive = ( v: unknown ): boolean =>
{
  return v == null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
};

const FEATURE_KEYS = new Set( [
  'event_id', 'config_name', 'room_id', 'is_bot', 'bot_id', 'req_trans_id', 'date_start', 'date_end', 'segment_name',
  'steps_total', 'total_points', 'room_size',
  'balance_coins', 'balance_hearts', 'balance_big_balls', 'balance_infinite_balls', 'balance_multi_balls', 'balance_fireworks',
  'balance_rockets', 'balance_mines', 'balance_infinite_hearts_min', 'balance_no_ads_min',
  'config_version', 'day_index', 'stars_current', 'tasks_total', 'tasks_completed', 'chests_total', 'chest_index', 'stars_required',
  'claimable_count', 'task_id', 'task_day', 'task_metric', 'task_param', 'task_target', 'task_progress', 'task_stars',
  'floors_total', 'current_floor', 'wins_progress', 'wins_required', 'duration_ms',
  'keys_count', 'keys_needed', 'prize_index',
  'release_id', 'release_rev', 'collection', 'collection_version', 'level_uid',
  'attempt_number', 'mass_attempt', 'mass_level', 'mass_applied', 'mass_source',
  'load_index',
  'shots_left', 'reward_type', 'reward_amount', 'rewards_count', 'board_no', 'grand_prize',
] );

const mapPropKey = ( key: string ): string =>
{
  const normalized = key.toLowerCase().replace(/[-\s]/g, "_");
  if ( normalized === 'game_number'
    || normalized === 'level_id'
    || normalized === 'difficulty_value'
    || normalized === 'start_moves'
    || normalized === 'result_points'
    || normalized === 'moves_done'
    || normalized === 'moves_purchased_times'
    || normalized === 'elements_left'
    || normalized === 'boosters_used_firework'
    || normalized === 'boosters_used_five_balls'
    || normalized === 'boosters_used_big_ball'
    || normalized === 'boosters_used_infinite_shots'
    || normalized === 'purchase_type'
    || normalized === 'is_test'
    || normalized === 'transaction_id'
    || FEATURE_KEYS.has( normalized ) )
  {
    return normalized;
  }
  return normalized.replace(/_/g, "");
};

const isForbiddenEventKey = ( key: string ): boolean =>
{
  const k = key.toLowerCase();
  return k === 'userid'
    || k === 'user_id'
    || k === 'sessionid'
    || k === 'session_id'
    || k === 'session'
    || k === 'timestamp'
    || k === 'actiontype'
    || k === 'action_type'
    || k === 'online'
    || k === 'country';
};

const buildProxyPayload = ( event: AnalyticsEvent, include_raw_event: boolean, is_qa_override?: boolean ): Record<string, unknown> =>
{
  const properties = event.properties ?? {};

  const source_id_from_props =
    toStringSafe( properties.sourceId ) ??
    toStringSafe( properties.source_id ) ??
    toStringSafe( properties.sourceid ) ??
    toStringSafe( properties.placementId ) ??
    toStringSafe( properties.placementid );

  const sign_up_ts =
    toIsoFromUnknown( properties.signUpTimestamp ) ??
    toIsoFromUnknown( properties.signupTimestamp ) ??
    toIsoFromUnknown( properties.signuptimestamp ) ??
    toIsoFromUnknown( properties.sign_up_timestamp ) ??
    toIso( event.ts );

  const user_level =
    toIntSafe( properties.userLevel ) ??
    toIntSafe( properties.userlevel ) ??
    0;

  const is_qa = typeof is_qa_override === 'boolean'
    ? is_qa_override
    : ( typeof event.context.is_prod === 'boolean' ? !event.context.is_prod : false );

  const payload: Record<string, unknown> = {
    name: String( event.name ),
    appname: toStringSafe( event.context.app ) ?? '',
    sourceid: source_id_from_props ?? String( event.name ),
    platform: toStringSafe( event.context.platform ) ?? '',
    version: normalizeVersion( event.context.app_version ),
    gamelocale: toStringSafe( event.context.locale ) ?? '',
    isqa: is_qa,
    device: toStringSafe( event.context.device_id ) ?? '',
    signuptimestamp: sign_up_ts,
    userlevel: user_level,
  };

  const reserved_keys = new Set( Object.keys( payload ).map( k => k.toLowerCase() ) );
  for ( const [ key, value ] of Object.entries( properties ) )
  {
    if ( isForbiddenEventKey( key ) ) continue;
    const mapped_key = mapPropKey( key );
    if ( isForbiddenEventKey( mapped_key ) ) continue;
    if ( reserved_keys.has( mapped_key.toLowerCase() ) ) continue;
    if ( !isPrimitive( value ) ) continue;
    payload[ mapped_key ] = value;
  }

  if ( include_raw_event )
  {
    payload.rawevent = event;
  }

  return payload;
};

const ATHENA_LOG_TAG = '[ATHENA_ANALYTICS]';

const debugLog = ( ...args: unknown[] ): void =>
{
  try
  {
    console.info( ...args );
  }
  catch {}
};

const warnLog = ( ...args: unknown[] ): void =>
{
  try
  {
    console.warn( ...args );
  }
  catch {}
};

const safeString = ( v: unknown ): string =>
{
  try
  {
    return String( v );
  } catch
  {
    return '';
  }
};

const truncateStringSafe = ( s: string, max_len: number ): string =>
{
  if ( max_len <= 0 ) return '';
  if ( s.length <= max_len ) return s;
  return `${s.slice( 0, Math.max( 0, max_len - 3 ) )}...`;
};

const classifyHttpStatus = ( status: number ): string =>
{
  if ( status >= 200 && status < 300 ) return '2xx';
  if ( status >= 300 && status < 400 ) return '3xx';
  if ( status >= 400 && status < 500 ) return '4xx';
  if ( status >= 500 && status < 600 ) return '5xx';
  if ( status >= 600 ) return '6xx+';
  return 'unknown';
};

const pushDebugPayload = ( payload: Record<string, unknown>, max_items: number ): void =>
{
  try
  {
    const g = globalThis as typeof globalThis & {
      __athenaAnalyticsLog?: Array<{ ts: number; payload: Record<string, unknown> }>;
    };
    const key = '__athenaAnalyticsLog';
    const arr = Array.isArray( g[ key ] ) ? g[ key ] : [];
    arr.push( { ts: Date.now(), payload } );
    while ( arr.length > max_items ) arr.shift();
    g[ key ] = arr;
  }
  catch {}
};

const normalizeBaseUrl = ( url: string ): string =>
{
  return url.replace( /\/+$/, '' );
};

const buildUrl = ( base_url: string, path: string, query?: Record<string, string> ): string =>
{
  const base = normalizeBaseUrl( base_url );
  const qp = query
    ? Object.entries( query )
      .map( ( [ k, v ] ) => `${encodeURIComponent( k )}=${encodeURIComponent( v )}` )
      .join( '&' )
    : '';
  const q = qp ? `?${qp}` : '';
  return `${base}/${path.replace( /^\/+/, '' )}${q}`;
};

const fetchWithTimeout = async ( url: string, init: RequestInit, timeout_ms?: number ): Promise<Response> =>
{
  const abort_controller = timeout_ms ? new AbortController() : null;
  const abort_timer = ( timeout_ms && abort_controller )
    ? setTimeout( () => abort_controller.abort(), timeout_ms )
    : null;

  try
  {
    return await fetch( url, { ...init, signal: abort_controller?.signal } );
  }
  finally
  {
    if ( abort_timer ) clearTimeout( abort_timer );
  }
};

const postJson = async ( url: string, payload: Record<string, unknown> | null, timeout_ms?: number ): Promise<Response> =>
{
  if ( payload == null )
  {
    return fetchWithTimeout( url, { method: 'POST' }, timeout_ms );
  }

  return fetchWithTimeout( url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: stringifyWithForcedFloatFields( payload ),
  }, timeout_ms );
};

export class AthenaProxyAnalyticsTransport implements AnalyticsTransport
{
  private readonly endpoint_url: string;
  private readonly timeout_ms?: number;
  private readonly include_raw_event: boolean;
  private readonly debug: boolean;
  private readonly debug_max_items: number;
  private readonly is_qa_override?: boolean;

  private session_id: string | null = null;
  private session_resolve: ( ( sid: string ) => void ) | null = null;
  private session_promise: Promise<string> = new Promise( resolve => { this.session_resolve = resolve; } );

  constructor( options: AthenaProxyTransportOptions )
  {
    this.endpoint_url = options.endpoint_url?.trim() ?? '';
    this.timeout_ms = options.timeout_ms;
    this.include_raw_event = options.include_raw_event ?? false;
    this.debug = options.debug ?? false;
    this.debug_max_items = Math.max( 0, Math.floor( options.debug_max_items ?? 50 ) );
    this.is_qa_override = options.is_qa_override;
  }

  setSessionId( session_id: string ): void
  {
    this.session_id = session_id;
    this.session_resolve?.( session_id );
    this.session_resolve = null;
    if ( this.debug ) debugLog( ATHENA_LOG_TAG, 'session_id set from WebSocket:', session_id );
  }

  private async sendEventInternal(
    websocket_payload: Record<string, unknown>,
    http_fallback_payload: Record<string, unknown>,
  ): Promise<void>
  {
    if ( this.session_id && await CustomEventsController.sendCustomEvent( websocket_payload ) )
      return;

    if ( !this.endpoint_url )
    {
      if ( this.debug ) debugLog( ATHENA_LOG_TAG, 'skip: endpoint_url is empty' );
      return;
    }

    const response = await postJson( this.endpoint_url, http_fallback_payload, this.timeout_ms );
    if ( !response.ok )
    {
      const body = await response.text().catch( () => '' );
      throw new Error( `Athena HTTP ${response.status} ${classifyHttpStatus( response.status )}: ${truncateStringSafe( body, 500 )}` );
    }
  }

  async send( event: AnalyticsEvent ): Promise<void>
  {
    const sid = this.session_id ?? await Promise.race( [
      this.session_promise,
      new Promise<null>( resolve => setTimeout( () => resolve( null ), 30_000 ) ),
    ] );

    if ( this.debug ) debugLog( ATHENA_LOG_TAG, 'event', String( event.name ), 'session_id', sid );

    const athena_payload = buildProxyPayload( event, this.include_raw_event, this.is_qa_override );
    const http_fallback_payload: Record<string, unknown> = {
      ...athena_payload,
      userid: toStringSafe( event.user_id ) ?? toStringSafe( event.properties?.userid ) ?? '',
      timestamp: toIso( event.ts ),
      sessionid: toStringSafe( event.session_id ) ?? '',
      country: toStringSafe( event.properties?.country ) ?? '',
    };
    if ( this.debug )
    {
      debugLog( ATHENA_LOG_TAG, 'send', athena_payload );
      if ( this.debug_max_items > 0 ) pushDebugPayload( athena_payload, this.debug_max_items );
    }

    await this.sendEventInternal( athena_payload, http_fallback_payload );
  }
}
